<template>
  <div>
    <b-container fluid>
      <b-row>
        <b-col sm="12" md="8">
          <h1>Offers to merchants</h1>
        </b-col>
        <b-col sm="12" md="4" align="right">
          <b-button variant="outline-warning" @click="showDangerZone = true">Danger Zone</b-button>
        </b-col>
      </b-row>
      <b-row>
        <b-col sm="12" md="3">
          <b-form-group label="Text" label-for="textFilter">
            <b-input
              id="textFilter"
              v-model="textFilter"
              size="sm"
              placeholder="Search in Item Id, Variant, Brand, Model"
            />
          </b-form-group>
        </b-col>
        <b-col sm="12" md="3">
          <b-form-group label="Brand" label-for="brandSelect">
            <b-form-select v-model="brandSelected" :options="brandOptions" size="sm">
              <template v-slot:first>
                <b-form-select-option value="" disabled>-- Please select an option --</b-form-select-option>
              </template>
            </b-form-select>
          </b-form-group>
        </b-col>
        <b-col sm="12" md="2">
          <b-form-group>
            <b-select v-model="onlyActive" :options="stateOptions"></b-select>
            <b-checkbox v-model="onlyActive" id="onlyActive">Only active</b-checkbox>
          </b-form-group>
        </b-col>
        <b-col sm="12" md="4">
          <b-button size="sm" block variant="secondary"
            @click="resetFilter()"
            :class="{ 'btn-primary': filterActive }"
            :disabled="!filterActive"
          >
            Reset Filter <b-icon-arrow-counterclockwise/>
          </b-button>
        </b-col>
      </b-row>
      <b-row>
        <b-col sm="6" md="6">
          <b-form-group label="Page Size" label-for="pageSize" label-cols="2">
            <b-form-radio-group
              id="pageSize"
              :options="[10, 20, 50]"
              v-model="pageSize"
              buttons
              size="sm"
              button-variant="outline-primary"
            />
          </b-form-group>
        </b-col>
        <b-col sm="6" align="right">
          Showing {{ firstResultInView }} - {{ lastResultInView }} of <strong>{{ numberOfFilteredOffers }}</strong> /
          Total
          {{ numberOfTotalOffers }}
        </b-col>
      </b-row>
      <b-table
        id="sellingOffersTable"
        striped
        outlined
        :items="filteredOffers"
        :fields="offerFields"
        :per-page="pageSize"
        :current-page="currentPage"
        :busy="status !== localConstants.STATUS_FINISHED_LOADING && status !== localConstants.STATUS_FINISHED_UPDATING"
      >
        <template v-slot:table-busy>
          <div class="text-center my-2" v-if="status === localConstants.STATUS_LOADING || status === localConstants.STATUS_UPDATING">
            <b-spinner class="align-middle" />&nbsp;
            <strong>Loading...</strong>
          </div>
        </template>
        <template v-slot:cell(image)="data">
          <b-img-lazy :id="data.item.itemId" :src="getThumbnail(data.item.image, 10)" alt="" />
          <b-tooltip :target="data.item.itemId" placement="left">
            <b-img :src="getThumbnail(data.item.image, 100)" fluid />
          </b-tooltip>
        </template>
        <template v-slot:cell(active)="data">
          <b-button variant="success" v-if="data.value" size="sm"><b-icon-check /></b-button>
          <b-button variant="warning" size="sm" v-else><b-icon-x /></b-button>
        </template>
        <template v-slot:cell(details)>
          <b-button variant="info" size="sm"><b-icon-eye-fill /></b-button>
        </template>
      </b-table>
      <b-pagination
        v-model="currentPage"
        :total-rows="filteredOffers.length"
        :per-page="pageSize"
        aria-controls="sellingOffersTable"
        align="center"
      />
    </b-container>
    <b-container>
      <b-row>
        <b-col cols="12" md="8"><h1>Offers to Merchants</h1></b-col>
        <b-col cols="12" md="4">
          <b-button variant="outline-warning" @click="showDangerZone = true" class="float-md-right">
            Danger Zone
          </b-button>
        </b-col>
      </b-row>
      <div v-if="status === localConstants.STATUS_LOADING">
        <b-row>
          <b-col class="text-center col-loading">
            <h3>Loading offers <b-spinner/></h3>
          </b-col>
        </b-row>
      </div>
      <div v-if="status === localConstants.STATUS_ERROR_LOADING">
        <b-row class="justify-content-center">
          <b-col cols="8" class="text-center col-loading">
            <div class="alert alert-danger">
              Temporary Error during loading. Please try to refresh the page and see if the error persists.
            </div>
          </b-col>
        </b-row>
      </div>
      <transition name="fade">
        <div v-if="status === localConstants.STATUS_FINISHED_LOADING">
          <b-row>
            <b-col cols="12" md="3">
              <b-form-group label="Text" label-for="textFilter">
                <b-input
                  id="textFilter"
                  v-model="textFilter"
                  size="sm"
                  placeholder="Search in Item Id, Variant, Brand, Model"
                />
              </b-form-group>
            </b-col>
            <b-col cols="12" md="3">
              <b-form-group label="Brand" label-for="brandSelect">
                <b-form-select v-model="brandSelected" :options="brandOptions" size="sm">
                  <template v-slot:first>
                    <b-form-select-option :value="null" disabled>-- Please select an option --</b-form-select-option>
                  </template>
                </b-form-select>
              </b-form-group>
            </b-col>
            <b-col sm="12" md="2">
              <b-form-group>
                <b-checkbox v-model="onlyActive" id="onlyActive">Only active</b-checkbox>
              </b-form-group>
            </b-col>
            <b-col sm="12" md="4">
              <b-button size="sm" block variant="secondary"
                @click="resetFilter()"
                :class="{ 'btn-primary': filterActive }"
                :disabled="!filterActive"
              >
                Reset Filter <b-icon-arrow-counterclockwise/>
              </b-button>
            </b-col>
          </b-row>
          <b-row>
            <b-col sm="6" md="6">
              <b-form-group label="Page Size" label-for="pageSize" label-cols="2">
                <b-form-radio-group
                  id="pageSize"
                  :options="[10, 20, 50]"
                  v-model="pageSize"
                  buttons
                  size="sm"
                  button-variant="outline-primary"
                />
              </b-form-group>
            </b-col>
            <b-col sm="6" align="right">
              Showing {{ firstResultInView }} - {{ lastResultInView }} of
              <strong>{{ numberOfFilteredOffers }}</strong> / Total
              {{ numberOfTotalOffers }}
            </b-col>
          </b-row>

          <hr />

          <!--
          Some extra padding, due to the col-bordered below, so the borders align with the heading
        -->
          <b-col class="col-container-offer-list-header">
            <div class="d-none d-sm-flex row row-header">
              <b-col cols="3" sm="1" class="col-header">Image</b-col>
              <b-col cols="3" sm="1" class="col-header">Variant</b-col>
              <b-col cols="3" sm="2" class="col-header">Brand</b-col>
              <b-col cols="3" sm="2" class="col-header">Model</b-col>
              <b-col cols="3" sm="1" class="col-header">Item Id</b-col>
              <b-col cols="3" sm="3" class="col-header">
                Reference, Condition, Year
              </b-col>
              <b-col cols="3" sm="1" class="col-header text-center">Active</b-col>
              <b-col cols="3" sm="1" class="col-header">Details</b-col>
            </div>
            <b-row v-for="offer in filteredPaginatedOffers" :key="offer.id">
              <admin-offer-row :offer="offer" />
            </b-row>
            <!--          <b-table ></b-table>-->
            <hr />
            <b-pagination :items="filteredOffers" :per-page="pageSize" :current-page="currentPage" />
            <pagination
              :items="filteredOffers"
              :currentPage="currentPage"
              :pageSize="pageSize"
              @changePage="changePage"
            />
          </b-col>
        </div>
      </transition>
      <admin-selling-offer-danger-zone
        @closeDangerZone="showDangerZone = false"
        @closeDangerZoneAndRefresh="closeDangerZoneAndRefresh()"
        v-if="showDangerZone"
        :offer="offer"
      />
    </b-container>
  </div>
</template>

<script>
import AdminOfferRow from 'Components/admin/sellingoffer/AdminOfferRow';
import store from 'src/store/index';
import Pagination from 'src/components/common/Pagination';
import AdminSellingOfferDangerZone from 'Components/admin/sellingoffer/AdminSellingOfferDangerZone';

export default {
  name: 'SellingOffer',
  components: {
    AdminOfferRow,
    AdminSellingOfferDangerZone,
    Pagination,
  },
  data: function() {
    return {
      showDangerZone: false,
      currentPage: 1,
      pageSize: 20,
      status: '',
      brandSelected: '',
      textFilter: '',
      onlyActive: false,
      stateOptions: [
        { text: 'All', value: '' },
        { text: 'Only active', value: true },
        { text: 'Only inactive', value: false },
      ],
      offerFields: [
        { key: 'image' },
        { key: 'variantKey' },
        { key: 'brand' },
        { key: 'model' },
        { key: 'itemId' },
        { key: 'createdAt' },
        { key: 'active' },
        { key: 'details' },
      ],
      offers: [],
      localConstants: {
        STATUS_LOADING: 'loading',
        STATUS_ERROR_LOADING: 'errorLoading',
        STATUS_FINISHED_LOADING: 'finishedLoading',
      },
    };
  },
  watch: {
    numberOfFilteredOffers() {
      if (this.numberOfFilteredPages < this.currentPage) {
        this.currentPage = Math.max(1, this.numberOfFilteredPages);
      }
    },
  },
  computed: {
    filterActive() {
      return this.textFilter !== '' || this.brandSelected !== '' || this.onlyActive !== false;
    },
    numberOfTotalOffers() {
      return this.offers.length;
    },
    numberOfFilteredPages() {
      return Math.ceil(this.filteredOffers.length / this.pageSize);
    },
    numberOfFilteredOffers() {
      return this.filteredOffers.length;
    },
    brandOptions() {
      let brands = [];
      let lowerCasedBrands = [];
      this.offers.forEach((offer) => {
        if (offer.brand === null || offer.brand === '') {
          return false;
        }
        if (!lowerCasedBrands.includes(offer.brand.toLowerCase())) {
          brands.push(offer.brand);
          lowerCasedBrands.push(offer.brand.toLowerCase());
        }
      });
      return brands.sort();
    },
    firstResultInView() {
      return Math.min(this.filteredOffers.length, (this.currentPage - 1) * this.pageSize + 1);
    },
    lastResultInView() {
      if (this.currentPage * this.pageSize > this.filteredOffers.length) {
        return this.filteredOffers.length;
      }
      return this.currentPage * this.pageSize;
    },
    filteredPaginatedOffers() {
      return this.filteredOffers.slice((this.currentPage - 1) * this.pageSize, this.currentPage * this.pageSize);
    },
    filteredOffers() {
      return this.offers.filter((offer) => {
        if (offer.brand === null) {
          offer.missingData = true;
          return true;
        }
        if (
          (offer.brand.toLowerCase().indexOf(this.textFilter.toLowerCase()) !== -1 ||
            offer.model.toLowerCase().indexOf(this.textFilter.toLowerCase()) !== -1 ||
            offer.itemId.toLowerCase().indexOf(this.textFilter.toLowerCase()) !== -1 ||
            offer.variantKey.toLowerCase().indexOf(this.textFilter.toLowerCase()) !== -1) &&
          offer.brand.toLowerCase().indexOf(this.brandSelected.toLowerCase()) !== -1
        ) {
          if (this.onlyActive === true) {
            if (offer.active === true) {
              return true;
            }
            return false;
          }
          return true;
        }
      });
    },
  },
  methods: {
    closeDangerZoneAndRefresh() {
      this.showDangerZone = false;
      this.loadOffers();
    },
    changePage(page) {
      this.currentPage = page;
    },
    resetFilter() {
      this.brandSelected = '';
      this.textFilter = '';
      this.onlyActive = false;
    },
    loadOffers() {
      this.status = this.localConstants.STATUS_LOADING;
      store
        .dispatch('loadSellingOffers')
        .then((response) => {
          this.status = this.localConstants.STATUS_FINISHED_LOADING;
          this.offers = response.data.offers;
        })
        .catch(() => {
          this.status = this.localConstants.STATUS_ERROR_LOADING;
        });
    },
    getThumbnail(url, width) {
      return (url !== undefined && url !== '')
        ? url.replace('cdn-products.chronext.com', 'chronexttime.imgix.net') +
            '?w=' +
            width +
            '&auto=format&fm=jpg&q=55&usm=50&usmrad=1.5&dpr=2&fit=clamp'
        : '';
    },
  },
  mounted() {
    this.loadOffers();
  },
};
</script>

<style lang="scss" scoped>
.page-size-selection .page-link {
  font-size: 0.85rem;
  padding: 0.35rem 0.5rem;
}

.row-header {
  padding-top: 3px;
  padding-bottom: 5px;
  border: 1px solid #fff;
  background-color: #777;
  color: #fff;
}

.col-container-offer-list-header {
  .col-header {
    padding-top: 3px;
    padding-bottom: 3px;
  }
}

.col-loading {
  padding: 30px 0;
}

.select-brand {
  width: 100%;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.4s;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}

.col-container-offer-list {
  border-top: 1px solid #cccccc;
}
</style>
